import type { Company, NoticeWithCompany } from "@dutyon/apis";
import {
  deleteNotice,
  getCompanies,
  getNotices,
  mapClientError,
  queryKeys,
  updateNotice,
} from "@dutyon/apis";
import {
  Button,
  Card,
  Group,
  LoadingOverlay,
  notifications,
  Select,
  Text,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useQueryState } from "nuqs";
import { useNavigate } from "react-router-dom";
import { NoticeTable } from "../../components/NoticeTable";

interface Props {
  target: "APP" | "DASHBOARD";
}

export function NoticeListPage({ target }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filterCompanyId, setFilterCompanyId] = useQueryState("companyId", {
    defaultValue: "SYSTEM",
  });

  const {
    data: notices = [],
    isLoading,
    isFetching,
  } = useQuery<NoticeWithCompany[]>({
    queryKey: queryKeys.notices.list(target, filterCompanyId),
    queryFn: () =>
      getNotices({
        target,
        companyId: filterCompanyId,
      }),
  });

  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: queryKeys.companies.all,
    queryFn: () => getCompanies(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteNotice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notices.all });
      notifications.show({
        title: "공지사항 삭제 완료",
        message: "공지사항이 성공적으로 삭제되었습니다.",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "삭제 실패",
        message: mapClientError(error, { fallback: "공지사항을 삭제하지 못했습니다." }).message,
        color: "red",
      });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "DRAFT" | "PUBLISHED" }) =>
      updateNotice(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notices.all });
      notifications.show({
        title: "배포 상태 변경 완료",
        message: "공지사항 배포 상태가 성공적으로 변경되었습니다.",
        color: "green",
      });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "실패",
        message: mapClientError(error, {
          fallback: "배포 상태 변경 중 오류가 발생했습니다.",
        }).message,
        color: "red",
      });
    },
  });

  const onEdit = (notice: NoticeWithCompany) => {
    const editPath =
      target === "APP" ? `/notices/app/${notice.id}/edit` : `/notices/dashboard/${notice.id}/edit`;
    navigate(editPath);
  };

  const onDelete = (id: string) => {
    if (window.confirm("정말 삭제하시겠습니까?")) {
      deleteMutation.mutate(id);
    }
  };

  const targetLabel = target === "APP" ? "앱" : "대시보드";

  return (
    <TitleContainer
      title={`${targetLabel} 공지사항 관리`}
      description={`${targetLabel} 클라이언트에 팝업/모달 형태로 나타날 공지사항을 관리합니다.`}
      rightSection={
        <Button
          leftSection={<Plus size={16} />}
          onClick={() => navigate(target === "APP" ? "/notices/app/new" : "/notices/dashboard/new")}
        >
          공지사항 등록
        </Button>
      }
    >
      {/* 병원 필터바 */}
      <Card withBorder mb="md" p="sm">
        <Group>
          <Text size="sm" fw={600}>
            병원 필터:
          </Text>
          <Select
            placeholder="전체 병원"
            data={[
              { value: "SYSTEM", label: "📢 시스템 전체 공지" },
              ...companies.map((c) => ({ value: c.id, label: c.name })),
            ]}
            value={filterCompanyId}
            onChange={(val) => setFilterCompanyId(val)}
            w={250}
          />
        </Group>
      </Card>

      <Card withBorder radius="md" p={0} pos="relative" style={{ overflow: "hidden" }}>
        <LoadingOverlay visible={isLoading || isFetching} overlayProps={{ blur: 1 }} />
        <NoticeTable
          notices={notices}
          isLoading={isLoading}
          onEdit={onEdit}
          onDelete={onDelete}
          onToggleStatus={(id, status) => toggleStatusMutation.mutate({ id, status })}
        />
      </Card>
    </TitleContainer>
  );
}
